import { Ikon } from '@studio/design-system/button';
import { Motions } from '@studio/design-system/motion';
import { Reorder, useDragging, useOrderedIds } from '@studio/design-system/motion/reorder';
import type { Preset as M_Preset } from '@studio/models/control/preset';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { isNotEmpty } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import { memo } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Preset } from './preset/index.js';
import type { ListProps } from './types.js';
import { Placeholder } from '#~/control/preset/components/card/index.js';
import { compareIds } from '#~/models/index.js';
import { Sel, STagNew } from '#~/stag/index.js';

/** @internal */
const Inner = ({ className, handler, presets, selectedIds, ...props }: ListProps): ReactElement => {
  // Drag/Drop
  const [orderedIds, setOrderedIds] = useOrderedIds(presets.map((µ) => µ.id));
  const [isDragging, setDragging] = useDragging();

  const handleDragStart = useLatestFn(() => {
    setDragging(true);
  });

  const handleDragEnd = useLatestFn(() => {
    setDragging(false);

    // Has the order actually changed?
    if (!compareIds(presets, orderedIds)) {
      handler.onReorder(orderedIds);
    }
  });

  const handleSelect = useLatestFn((presetId: M_Preset['id']) => {
    // Ignore click events while dragging.
    if (!isDragging) {
      handler.onSelect(presetId);
    }
  });

  // Override `onSelect` handler.
  const passthru = useLazyRef(() => ({
    ...handler,
    onSelect: handleSelect
  })).current;

  return (
    <div className={twMerge('m-16 flex gap-16 [&>*]:flex-[0_0_208px]', className)} {...props}>
      {isNotEmpty(presets) ? (
        <Reorder.Group
          axis='x'
          className={twMerge('flex gap-16')}
          layoutScroll
          onReorder={setOrderedIds}
          values={orderedIds}
        >
          {orderedIds.map((µ) => {
            const preset = presets.find((p) => p.id === µ);

            // Sanity check
            if (!preset) {
              return null;
            }

            return (
              <Reorder.Item
                key={preset.id}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                value={preset.id}
                {...Motions.Drag}
                {...Motions.Fade}
              >
                {/* Be very careful w/ memoization here! */}
                <Preset
                  className='max-w-[208px]'
                  handler={passthru}
                  isSelected={selectedIds.includes(preset.id)}
                  preset={preset}
                />
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      ) : (
        <Placeholder onClick={handler.onAdd} />
      )}

      <div className='!basis-auto self-center pr-16'>
        {/* Per rule: playwright-stag-selectors-only (ai.knowledge/platform) —
            the hardware preset-recall spec SAVEs through this real control
            rather than seeding records, so it needs a stable STag. */}
        <Ikon
          className='w-32 text-lg'
          icon='plus'
          onClick={handler.onAdd}
          {...STagNew(Sel.MainRoomBottomTrayPresets, 'preset-add')}
        />
      </div>
    </div>
  );
};

/** Position preset `<List />` component. */
export const List = memo(Inner, dequal);
